import React from "react";
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Avatar,
  Box,
  Chip,
  SvgIcon,
  ListSubheader,
  IconButton,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  Memory as DefaultIcon,
  Edit as EditIcon,
  PlayArrow as TestIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useUser } from "../contexts/useUser";

interface LLMModel {
  id: string;
  vendor: string;
  name: string;
  host?: string;
  description?: string;
  inputTokenPrice?: number;
  outputTokenPrice?: number;
  performanceRating?: string;
  defaultServiceGroup?: string;
  defaultEnabled?: boolean;
  status: string;
  modelKey?: string;
  modelKeyLatest?: string;
  supportsSearch?: boolean;
  thinkingSupport?: boolean;
}

interface ServiceGroupCardProps {
  title: string;
  description: string;
  models: LLMModel[];
  selectedModels: string[];
  onModelToggle: (modelKey: string) => void;
  isAdmin?: boolean;
  onAdminEditModel?: (modelId: string) => void;
  onModelTest?: (model: LLMModel) => Promise<boolean>;
  testingModels?: Set<string>;
  testResults?: Map<string, boolean>;
  hasAmazonApiKey?: boolean;
  hasAzureApiKey?: boolean;
}

const getVendorIcon = (vendor: string) => {
  const vendorLower = vendor.toLowerCase();

  if (vendorLower === "openai") {
    return (
      <SvgIcon>
        <path d="M21.55 10.004a5.416 5.416 0 00-.478-4.501c-1.217-2.09-3.662-3.166-6.05-2.66A5.59 5.59 0 0010.831 1C8.39.995 6.224 2.546 5.473 4.838A5.553 5.553 0 001.76 7.496a5.487 5.487 0 00.691 6.5 5.416 5.416 0 00.477 4.502c1.217 2.09 3.662 3.165 6.05 2.66A5.586 5.586 0 0013.168 23c2.443.006 4.61-1.546 5.361-3.84a5.553 5.553 0 003.715-2.66 5.488 5.488 0 00-.693-6.497v.001zm-8.381 11.558a4.199 4.199 0 01-2.675-.954c.034-.018.093-.05.132-.074l4.44-2.53a.71.71 0 00.364-.623v-6.176l1.877 1.069c.02.01.033.029.036.05v5.115c-.003 2.274-1.87 4.118-4.174 4.123zM4.192 17.78a4.059 4.059 0 01-.498-2.763c.032.02.09.055.131.078l4.44 2.53c.225.13.504.13.73 0l5.42-3.088v2.138a.068.068 0 01-.027.057L9.9 19.288c-1.999 1.136-4.552.46-5.707-1.51h-.001zM3.023 8.216A4.15 4.15 0 015.198 6.41l-.002.151v5.06a.711.711 0 00.364.624l5.42 3.087-1.876 1.07a.067.067 0 01-.063.005l-4.489-2.559c-1.995-1.14-2.679-3.658-1.53-5.63h.001zm15.417 3.54l-5.42-3.088L14.896 7.6a.067.067 0 01.063-.006l4.489 2.557c1.998 1.14 2.683 3.662 1.529 5.633a4.163 4.163 0 01-2.174 1.807V12.38a.71.71 0 00-.363-.623zm1.867-2.773a6.04 6.04 0 00-.132-.078l-4.44-2.53a.731.731 0 00-.729 0l-5.42 3.088V7.325a.068.068 0 01.027-.057L14.1 4.713c2-1.137 4.555-.46 5.707 1.513.487.833.664 1.809.499 2.757h.001zm-11.741 3.81l-1.877-1.068a.065.065 0 01-.036-.051V6.559c.001-2.277 1.873-4.122 4.181-4.12.976 0 1.92.338 2.671.954-.034.018-.092.05-.131.073l-4.44 2.53a.71.71 0 00-.365.623l-.003 6.173v.002zm1.02-2.168L12 9.25l2.414 1.375v2.75L12 14.75l-2.415-1.375v-2.75z"></path>
      </SvgIcon>
    );
  } else if (vendorLower === "anthropic") {
    return (
      <SvgIcon>
        <path d="M13.827 3.52h3.603L24 20h-3.603l-6.57-16.48zm-7.258 0h3.767L16.906 20h-3.674l-1.343-3.461H5.017l-1.344 3.46H0L6.57 3.522zm4.132 9.959L8.453 7.687 6.205 13.48H10.7z"></path>
      </SvgIcon>
    );  } else if (vendorLower === "amazon") {
    return (
      <SvgIcon>
        <defs>
          <linearGradient id="lobe-icons-bedrock-fill" x1="80%" x2="20%" y1="20%" y2="80%">
            <stop offset="0%" stopColor="#6350FB"></stop>
            <stop offset="50%" stopColor="#3D8FFF"></stop>
            <stop offset="100%" stopColor="#9AD8F8"></stop>
          </linearGradient>
        </defs>
        <path d="M13.05 15.513h3.08c.214 0 .389.177.389.394v1.82a1.704 1.704 0 011.296 1.661c0 .943-.755 1.708-1.685 1.708-.931 0-1.686-.765-1.686-1.708 0-.807.554-1.484 1.297-1.662v-1.425h-2.69v4.663a.395.395 0 01-.188.338l-2.69 1.641a.385.385 0 01-.405-.002l-4.926-3.086a.395.395 0 01-.185-.336V16.3L2.196 14.87A.395.395 0 012 14.555L2 14.528V9.406c0-.14.073-.27.192-.34l2.465-1.462V4.448c0-.129.062-.249.165-.322l.021-.014L9.77 1.058a.385.385 0 01.407 0l2.69 1.675a.395.395 0 01.185.336V7.6h3.856V5.683a1.704 1.704 0 01-1.296-1.662c0-.943.755-1.708 1.685-1.708.931 0 1.685.765 1.685 1.708 0 .807-.553 1.484-1.296 1.662v2.311a.391.391 0 01-.389.394h-4.245v1.806h6.624a1.69 1.69 0 011.64-1.313c.93 0 1.685.764 1.685 1.707 0 .943-.754 1.708-1.685 1.708a1.69 1.69 0 01-1.64-1.314H13.05v1.937h4.953l.915 1.18a1.66 1.66 0 01.84-.227c.931 0 1.685.764 1.685 1.707 0 .943-.754 1.708-1.685 1.708-.93 0-1.685-.765-1.685-1.708 0-.346.102-.668.276-.937l-.724-.935H13.05v1.806zM9.973 1.856L7.93 3.122V6.09h-.778V3.604L5.435 4.669v2.945l2.11 1.36L9.712 7.61V5.334h.778V7.83c0 .136-.07.263-.184.335L7.963 9.638v2.081l1.422 1.009-.446.646-1.406-.998-1.53 1.005-.423-.66 1.605-1.055v-1.99L5.038 8.29l-2.26 1.34v1.676l1.972-1.189.398.677-2.37 1.429V14.3l2.166 1.258 2.27-1.368.397.677-2.176 1.311V19.3l1.876 1.175 2.365-1.426.398.678-2.017 1.216 1.918 1.201 2.298-1.403v-5.78l-4.758 2.893-.4-.675 5.158-3.136V3.289L9.972 1.856zM16.13 18.47a.913.913 0 00-.908.92c0 .507.406.918.908.918a.913.913 0 00.907-.919.913.913 0 00-.907-.92zm3.63-3.81a.913.913 0 00-.908.92c0 .508.406.92.907.92a.913.913 0 00.908-.92.913.913 0 00-.908-.92zm1.555-4.99a.913.913 0 00-.908.92c0 .507.407.918.908.918a.913.913 0 00.907-.919.913.913 0 00-.907-.92zM17.296 3.1a.913.913 0 00-.907.92c0 .508.406.92.907.92a.913.913 0 00.908-.92.913.913 0 00-.908-.92z" fill="url(#lobe-icons-bedrock-fill)" fillRule="nonzero"></path>
      </SvgIcon>
    );
  } else if (vendorLower === "google") {
    return (
      <SvgIcon>
        <path d="M12 24A14.304 14.304 0 000 12 14.304 14.304 0 0012 0a14.305 14.305 0 0012 12 14.305 14.305 0 00-12 12"></path>
      </SvgIcon>
    );
  } else if (vendorLower === "ollama") {
    return (
      <SvgIcon>
        <path d="M7.905 1.09c.216.085.411.225.588.41.295.306.544.744.734 1.263.191.522.315 1.1.362 1.68a5.054 5.054 0 012.049-.636l.051-.004c.87-.07 1.73.087 2.48.474.101.053.2.11.297.17.05-.569.172-1.134.36-1.644.19-.52.439-.957.733-1.264a1.67 1.67 0 01.589-.41c.257-.1.53-.118.796-.042.401.114.745.368 1.016.737.248.337.434.769.561 1.287.23.934.27 2.163.115 3.645l.053.04.026.019c.757.576 1.284 1.397 1.563 2.35.435 1.487.216 3.155-.534 4.088l-.018.021.002.003c.417.762.67 1.567.724 2.4l.002.03c.064 1.065-.2 2.137-.814 3.19l-.007.01.01.024c.472 1.157.62 2.322.438 3.486l-.006.039a.651.651 0 01-.747.536.648.648 0 01-.54-.742c.167-1.033.01-2.069-.48-3.123a.643.643 0 01.04-.617l.004-.006c.604-.924.854-1.83.8-2.72-.046-.779-.325-1.544-.8-2.273a.644.644 0 01.18-.886l.009-.006c.243-.159.467-.565.58-1.12a4.229 4.229 0 00-.095-1.974c-.205-.7-.58-1.284-1.105-1.683-.595-.454-1.383-.673-2.38-.61a.653.653 0 01-.632-.371c-.314-.665-.772-1.141-1.343-1.436a3.288 3.288 0 00-1.772-.332c-1.245.099-2.343.801-2.67 1.686a.652.652 0 01-.61.425c-1.067.002-1.893.252-2.497.703-.522.39-.878.935-1.066 1.588a4.07 4.07 0 00-.068 1.886c.112.558.331 1.02.582 1.269l.008.007c.212.207.257.53.109.785-.36.622-.629 1.549-.673 2.44-.05 1.018.186 1.902.719 2.536l.016.019a.643.643 0 01.095.69c-.576 1.236-.753 2.252-.562 3.052a.652.652 0 01-1.269.298c-.243-1.018-.078-2.184.473-3.498l.014-.035-.008-.012a4.339 4.339 0 01-.598-1.309l-.005-.019a5.764 5.764 0 01-.177-1.785c.044-.91.278-1.842.622-2.59l.012-.026-.002-.002c-.293-.418-.51-.953-.63-1.545l-.005-.024a5.352 5.352 0 01.093-2.49c.262-.915.777-1.701 1.536-2.269.06-.045.123-.09.186-.132-.159-1.493-.119-2.73.112-3.67.127-.518.314-.95.562-1.287.27-.368.614-.622 1.015-.737.266-.076.54-.059.797.042zm4.116 9.09c.936 0 1.8.313 2.446.855.63.527 1.005 1.235 1.005 1.94 0 .888-.406 1.58-1.133 2.022-.62.375-1.451.557-2.403.557-1.009 0-1.871-.259-2.493-.734-.617-.47-.963-1.13-.963-1.845 0-.707.398-1.417 1.056-1.946.668-.537 1.55-.849 2.485-.849zm0 .896a3.07 3.07 0 00-1.916.65c-.461.37-.722.835-.722 1.25 0 .428.21.829.61 1.134.455.347 1.124.548 1.943.548.799 0 1.473-.147 1.932-.426.463-.28.7-.686.7-1.257 0-.423-.246-.89-.683-1.256-.484-.405-1.14-.643-1.864-.643zm.662 1.21l.004.004c.12.151.095.37-.056.49l-.292.23v.446a.375.375 0 01-.376.373.375.375 0 01-.376-.373v-.46l-.271-.218a.347.347 0 01-.052-.49.353.353 0 01.494-.051l.215.172.22-.174a.353.353 0 01.49.051zm-5.04-1.919c.478 0 .867.39.867.871a.87.87 0 01-.868.871.87.87 0 01-.867-.87.87.87 0 01.867-.872zm8.706 0c.48 0 .868.39.868.871a.87.87 0 01-.868.871.87.87 0 01-.867-.87.87.87 0 01.867-.872zM7.44 2.3l-.003.002a.659.659 0 00-.285.238l-.005.006c-.138.189-.258.467-.348.832-.17.692-.216 1.631-.124 2.782.43-.128.899-.208 1.404-.237l.01-.001.019-.034c.046-.082.095-.161.148-.239.123-.771.022-1.692-.253-2.444-.134-.364-.297-.65-.453-.813a.628.628 0 00-.107-.09L7.44 2.3zm9.174.04l-.002.001a.628.628 0 00-.107.09c-.156.163-.32.45-.453.814-.29.794-.387 1.776-.23 2.572l.058.097.008.014h.03a5.184 5.184 0 011.466.212c.086-1.124.038-2.043-.128-2.722-.09-.365-.21-.643-.349-.832l-.004-.006a.659.659 0 00-.285-.239h-.004z"></path>
      </SvgIcon>
    );
  } else if (vendorLower === "microsoft") {
    return (
      <SvgIcon>
        <path
          d="M18.397 15.296H7.4a.51.51 0 00-.347.882l7.066 6.595c.206.192.477.298.758.298h6.226l-2.706-7.775z"
          fillOpacity=".75"
        ></path>
        <path
          d="M8.295.857c-.477 0-.9.304-1.053.756L.495 21.605a1.11 1.11 0 001.052 1.466h5.43c.477 0 .9-.304 1.053-.755l1.341-3.975-2.318-2.163a.51.51 0 01.347-.882h3L15.271.857H8.295z"
          fillOpacity=".5"
        ></path>
        <path d="M17.193 1.613a1.11 1.11 0 00-1.052-.756h-7.81.035c.477 0 .9.304 1.052.756l6.748 19.992a1.11 1.11 0 01-1.052 1.466h-.12 7.895a1.11 1.11 0 001.052-1.466L17.193 1.613z"></path>
      </SvgIcon>
    );  } else if (vendorLower === "mistral") {
    return (
      <SvgIcon>
        <path d="M3.428 3.4h3.429v3.428H3.428V3.4zm13.714 0h3.43v3.428h-3.43V3.4z" fill="gold"></path>
        <path d="M3.428 6.828h6.857v3.429H3.429V6.828zm10.286 0h6.857v3.429h-6.857V6.828z" fill="#FFAF00"></path>
        <path d="M3.428 10.258h17.144v3.428H3.428v-3.428z" fill="#FF8205"></path>
        <path d="M3.428 13.686h3.429v3.428H3.428v-3.428zm6.858 0h3.429v3.428h-3.429v-3.428zm6.856 0h3.43v3.428h-3.43v-3.428z" fill="#FA500F"></path>
        <path d="M0 17.114h10.286v3.429H0v-3.429zm13.714 0H24v3.429H13.714v-3.429z" fill="#E10500"></path>
      </SvgIcon>
    );
  } else if (vendorLower === "meta") {
    return (
      <SvgIcon>
        <defs>
          <linearGradient id="lobe-icons-meta-fill-0" x1="75.897%" x2="26.312%" y1="89.199%" y2="12.194%">
            <stop offset=".06%" stopColor="#0867DF"></stop>
            <stop offset="45.39%" stopColor="#0668E1"></stop>
            <stop offset="85.91%" stopColor="#0064E0"></stop>
          </linearGradient>
          <linearGradient id="lobe-icons-meta-fill-1" x1="21.67%" x2="97.068%" y1="75.874%" y2="23.985%">
            <stop offset="13.23%" stopColor="#0064DF"></stop>
            <stop offset="99.88%" stopColor="#0064E0"></stop>
          </linearGradient>
          <linearGradient id="lobe-icons-meta-fill-2" x1="38.263%" x2="60.895%" y1="89.127%" y2="16.131%">
            <stop offset="1.47%" stopColor="#0072EC"></stop>
            <stop offset="68.81%" stopColor="#0064DF"></stop>
          </linearGradient>
          <linearGradient id="lobe-icons-meta-fill-3" x1="47.032%" x2="52.15%" y1="90.19%" y2="15.745%">
            <stop offset="7.31%" stopColor="#007CF6"></stop>
            <stop offset="99.43%" stopColor="#0072EC"></stop>
          </linearGradient>
          <linearGradient id="lobe-icons-meta-fill-4" x1="52.155%" x2="47.591%" y1="58.301%" y2="37.004%">
            <stop offset="7.31%" stopColor="#007FF9"></stop>
            <stop offset="100%" stopColor="#007CF6"></stop>
          </linearGradient>
          <linearGradient id="lobe-icons-meta-fill-5" x1="37.689%" x2="61.961%" y1="12.502%" y2="63.624%">
            <stop offset="7.31%" stopColor="#007FF9"></stop>
            <stop offset="100%" stopColor="#0082FB"></stop>
          </linearGradient>
          <linearGradient id="lobe-icons-meta-fill-6" x1="34.808%" x2="62.313%" y1="68.859%" y2="23.174%">
            <stop offset="27.99%" stopColor="#007FF8"></stop>
            <stop offset="91.41%" stopColor="#0082FB"></stop>
          </linearGradient>
          <linearGradient id="lobe-icons-meta-fill-7" x1="43.762%" x2="57.602%" y1="6.235%" y2="98.514%">
            <stop offset="0%" stopColor="#0082FB"></stop>
            <stop offset="99.95%" stopColor="#0081FA"></stop>
          </linearGradient>
          <linearGradient id="lobe-icons-meta-fill-8" x1="60.055%" x2="39.88%" y1="4.661%" y2="69.077%">
            <stop offset="6.19%" stopColor="#0081FA"></stop>
            <stop offset="100%" stopColor="#0080F9"></stop>
          </linearGradient>
          <linearGradient id="lobe-icons-meta-fill-9" x1="30.282%" x2="61.081%" y1="59.32%" y2="33.244%">
            <stop offset="0%" stopColor="#027AF3"></stop>
            <stop offset="100%" stopColor="#0080F9"></stop>
          </linearGradient>
          <linearGradient id="lobe-icons-meta-fill-10" x1="20.433%" x2="82.112%" y1="50.001%" y2="50.001%">
            <stop offset="0%" stopColor="#0377EF"></stop>
            <stop offset="99.94%" stopColor="#0279F1"></stop>
          </linearGradient>
          <linearGradient id="lobe-icons-meta-fill-11" x1="40.303%" x2="72.394%" y1="35.298%" y2="57.811%">
            <stop offset=".19%" stopColor="#0471E9"></stop>
            <stop offset="100%" stopColor="#0377EF"></stop>
          </linearGradient>
          <linearGradient id="lobe-icons-meta-fill-12" x1="32.254%" x2="68.003%" y1="19.719%" y2="84.908%">
            <stop offset="27.65%" stopColor="#0867DF"></stop>
            <stop offset="100%" stopColor="#0471E9"></stop>
          </linearGradient>
        </defs>
        <g fill="none" fillRule="nonzero">
          <path d="M6.897 4h-.024l-.031 2.615h.022c1.715 0 3.046 1.357 5.94 6.246l.175.297.012.02 1.62-2.438-.012-.019a48.763 48.763 0 00-1.098-1.716 28.01 28.01 0 00-1.175-1.629C10.413 4.932 8.812 4 6.896 4z" fill="url(#lobe-icons-meta-fill-0)"></path>
          <path d="M6.873 4C4.95 4.01 3.247 5.258 2.02 7.17a4.352 4.352 0 00-.01.017l2.254 1.231.011-.017c.718-1.083 1.61-1.774 2.568-1.785h.021L6.896 4h-.023z" fill="url(#lobe-icons-meta-fill-1)"></path>
          <path d="M2.019 7.17l-.011.017C1.2 8.447.598 9.995.274 11.664l-.005.022 2.534.6.004-.022c.27-1.467.786-2.828 1.456-3.845l.011-.017L2.02 7.17z" fill="url(#lobe-icons-meta-fill-2)"></path>
          <path d="M2.807 12.264l-2.533-.6-.005.022c-.177.918-.267 1.851-.269 2.786v.023l2.598.233v-.023a12.591 12.591 0 01.21-2.44z" fill="url(#lobe-icons-meta-fill-3)"></path>
          <path d="M2.677 15.537a5.462 5.462 0 01-.079-.813v-.022L0 14.468v.024a8.89 8.89 0 00.146 1.652l2.535-.585a4.106 4.106 0 01-.004-.022z" fill="url(#lobe-icons-meta-fill-4)"></path>
          <path d="M3.27 16.89c-.284-.31-.484-.756-.589-1.328l-.004-.021-2.535.585.004.021c.192 1.01.568 1.85 1.106 2.487l.014.017 2.018-1.745a2.106 2.106 0 01-.015-.016z" fill="url(#lobe-icons-meta-fill-5)"></path>
          <path d="M10.78 9.654c-1.528 2.35-2.454 3.825-2.454 3.825-2.035 3.2-2.739 3.917-3.871 3.917a1.545 1.545 0 01-1.186-.508l-2.017 1.744.014.017C2.01 19.518 3.058 20 4.356 20c1.963 0 3.374-.928 5.884-5.33l1.766-3.13a41.283 41.283 0 00-1.227-1.886z" fill="#0082FB"></path>
          <path d="M13.502 5.946l-.016.016c-.4.43-.786.908-1.16 1.416.378.483.768 1.024 1.175 1.63.48-.743.928-1.345 1.367-1.807l.016-.016-1.382-1.24z" fill="url(#lobe-icons-meta-fill-6)"></path>
          <path d="M20.918 5.713C19.853 4.633 18.583 4 17.225 4c-1.432 0-2.637.787-3.723 1.944l-.016.016 1.382 1.24.016-.017c.715-.747 1.408-1.12 2.176-1.12.826 0 1.6.39 2.27 1.075l.015.016 1.589-1.425-.016-.016z" fill="#0082FB"></path>
          <path d="M23.998 14.125c-.06-3.467-1.27-6.566-3.064-8.396l-.016-.016-1.588 1.424.015.016c1.35 1.392 2.277 3.98 2.361 6.971v.023h2.292v-.022z" fill="url(#lobe-icons-meta-fill-7)"></path>
          <path d="M23.998 14.15v-.023h-2.292v.022c.004.14.006.282.006.424 0 .815-.121 1.474-.368 1.95l-.011.022 1.708 1.782.013-.02c.62-.96.946-2.293.946-3.91 0-.083 0-.165-.002-.247z" fill="url(#lobe-icons-meta-fill-8)"></path>
          <path d="M21.344 16.52l-.011.02c-.214.402-.519.67-.917.787l.778 2.462a3.493 3.493 0 00.438-.182 3.558 3.558 0 001.366-1.218l.044-.065.012-.02-1.71-1.784z" fill="url(#lobe-icons-meta-fill-9)"></path>
          <path d="M19.92 17.393c-.262 0-.492-.039-.718-.14l-.798 2.522c.449.153.927.222 1.46.222.492 0 .943-.073 1.352-.215l-.78-2.462c-.167.05-.341.075-.517.073z" fill="url(#lobe-icons-meta-fill-10)"></path>
          <path d="M18.323 16.534l-.014-.017-1.836 1.914.016.017c.637.682 1.246 1.105 1.937 1.337l.797-2.52c-.291-.125-.573-.353-.9-.731z" fill="url(#lobe-icons-meta-fill-11)"></path>
          <path d="M18.309 16.515c-.55-.642-1.232-1.712-2.303-3.44l-1.396-2.336-.011-.02-1.62 2.438.012.02.989 1.668c.959 1.61 1.74 2.774 2.493 3.585l.016.016 1.834-1.914a2.353 2.353 0 01-.014-.017z" fill="url(#lobe-icons-meta-fill-12)"></path>        </g>
      </SvgIcon>
    );
  } else if (vendorLower === "deepseek") {
    return (
      <SvgIcon>
        <path d="M23.748 4.482c-.254-.124-.364.113-.512.234-.051.039-.094.09-.137.136-.372.397-.806.657-1.373.626-.829-.046-1.537.214-2.163.848-.133-.782-.575-1.248-1.247-1.548-.352-.156-.708-.311-.955-.65-.172-.241-.219-.51-.305-.774-.055-.16-.11-.323-.293-.35-.2-.031-.278.136-.356.276-.313.572-.434 1.202-.422 1.84.027 1.436.633 2.58 1.838 3.393.137.093.172.187.129.323-.082.28-.18.552-.266.833-.055.179-.137.217-.329.14a5.526 5.526 0 01-1.736-1.18c-.857-.828-1.631-1.742-2.597-2.458a11.365 11.365 0 00-.689-.471c-.985-.957.13-1.743.388-1.836.27-.098.093-.432-.779-.428-.872.004-1.67.295-2.687.684a3.055 3.055 0 01-.465.137 9.597 9.597 0 00-2.883-.102c-1.885.21-3.39 1.102-4.497 2.623C.082 8.606-.231 10.684.152 12.85c.403 2.284 1.569 4.175 3.36 5.653 1.858 1.533 3.997 2.284 6.438 2.14 1.482-.085 3.133-.284 4.994-1.86.47.234.962.327 1.78.397.63.059 1.236-.03 1.705-.128.735-.156.684-.837.419-.961-2.155-1.004-1.682-.595-2.113-.926 1.096-1.296 2.746-2.642 3.392-7.003.05-.347.007-.565 0-.845-.004-.17.035-.237.23-.256a4.173 4.173 0 001.545-.475c1.396-.763 1.96-2.015 2.093-3.517.02-.23-.004-.467-.247-.588zM11.581 18c-2.089-1.642-3.102-2.183-3.52-2.16-.392.024-.321.471-.235.763.09.288.207.486.371.739.114.167.192.416-.113.603-.673.416-1.842-.14-1.897-.167-1.361-.802-2.5-1.86-3.301-3.307-.774-1.393-1.224-2.887-1.298-4.482-.02-.386.093-.522.477-.592a4.696 4.696 0 011.529-.039c2.132.312 3.946 1.265 5.468 2.774.868.86 1.525 1.887 2.202 2.891.72 1.066 1.494 2.082 2.48 2.914.348.292.625.514.891.677-.802.09-2.14.11-3.054-.614zm1-6.44a.306.306 0 01.415-.287.302.302 0 01.2.288.306.306 0 01-.31.307.303.303 0 01-.304-.308zm3.11 1.596c-.2.081-.399.151-.59.16a1.245 1.245 0 01-.798-.254c-.274-.23-.47-.358-.552-.758a1.73 1.73 0 01.016-.588c.07-.327-.008-.537-.239-.727-.187-.156-.426-.199-.688-.199a.559.559 0 01-.254-.078c-.11-.054-.2-.19-.114-.358.028-.054.16-.186.192-.21.356-.202.767-.136 1.146.016.352.144.618.408 1.001.782.391.451.462.576.685.914.176.265.336.537.445.848.067.195-.019.354-.25.452z" fill="#4D6BFE"></path>
      </SvgIcon>
    );
  }

  return <DefaultIcon />;
};

const getVendorColor = (vendor: string) => {
  const vendorLower = vendor.toLowerCase();

  if (vendorLower === "openai") {
    return "#00A67E";
  } else if (vendorLower === "anthropic") {
    return "#D97757";
  } else if (vendorLower === "google") {
    return "#4285F4";
  } else if (vendorLower === "microsoft" || vendorLower === "azure") {
    return "#0078D4";
  }

  return "#666";
};

const formatPrice = (price?: number) => {
  if (!price) return "Free";
  return `$${price.toFixed(3)}/M tokens`;
};

const formatTokenPrices = (inputPrice?: number, outputPrice?: number) => {
  const input = inputPrice ? `In: ${formatPrice(inputPrice)}` : "";
  const output = outputPrice ? `Out: ${formatPrice(outputPrice)}` : "";

  if (input && output) {
    return `${input} • ${output}`;
  } else if (input) {
    return input;
  } else if (output) {
    return output;
  } else {
    return "Free";
  }
};

const getModelKey = (model: LLMModel) => {
  // Use the model ID as the key - simple and reliable
  return model.id;
};

// Group models by host, then by vendor and model name
interface HostGroup {
  host: string;
  modelGroups: ModelGroup[];
}

interface ModelGroup {
  vendor: string;
  name: string;
  models: LLMModel[];
}

const groupModelsByHost = (models: LLMModel[]): HostGroup[] => {
  const hostGroups: { [key: string]: LLMModel[] } = {};

  // First group by host
  models.forEach((model) => {
    const hostKey = model.host || "Default";
    if (!hostGroups[hostKey]) {
      hostGroups[hostKey] = [];
    }
    hostGroups[hostKey].push(model);
  });

  // Define the preferred order for hosts
  const hostOrder = [
    "Anthropic",
    "OpenAI",
    "Google",
    "Mistral",
    "Amazon",
    "Azure",
    "Default",
  ];

  // Sort hosts according to the preferred order, with unknown hosts at the end
  const sortedHosts = Object.keys(hostGroups).sort((a, b) => {
    const indexA = hostOrder.indexOf(a);
    const indexB = hostOrder.indexOf(b);

    // If both hosts are in the order list, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // If only one is in the order list, prioritize it
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    // If neither is in the order list, sort alphabetically
    return a.localeCompare(b);
  });

  // Then group each host's models by vendor:name
  return sortedHosts.map((host) => {
    const hostModels = hostGroups[host];
    const modelGroups: { [key: string]: ModelGroup } = {};

    hostModels.forEach((model) => {
      const groupKey = `${model.vendor}:${model.name}`;

      if (!modelGroups[groupKey]) {
        modelGroups[groupKey] = {
          vendor: model.vendor,
          name: model.name,
          models: [],
        };
      }

      modelGroups[groupKey].models.push(model);
    });

    return {
      host,
      modelGroups: Object.values(modelGroups),
    };
  });
};

export default function ServiceGroupCard({
  title,
  description,
  models,
  selectedModels,
  onModelToggle,
  isAdmin = false,
  onAdminEditModel,
  onModelTest,
  testingModels = new Set(),
  testResults = new Map(),
  hasAmazonApiKey = false,
  hasAzureApiKey = false,
}: ServiceGroupCardProps) {
  const { project } = useUser();

  const selectedCount = models.filter((model) =>
    selectedModels.includes(getModelKey(model))
  ).length;

  const handleTestModel = async (model: LLMModel) => {
    if (onModelTest) {
      await onModelTest(model);
    }
  };

  return (
    <Card
      sx={{
        height: "100%",
        opacity: 1,
        backgroundColor: "background.default",
      }}
    >
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              width: "100%",
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">{title}</Typography>
              <Box>
                

                <Button
                  sx={{ marginRight: 1 }}
                  onClick={(event) => {
                    event.stopPropagation();
                    navigator.clipboard.writeText(
                      `// import the hook
              import { useLLM } from "llmasaservice-client";
              
              // in your component, destructure the send/response/idle functions from the hook
              const { send, response, idle } = useLLM({
                project_id: "${project?.id ?? ""}", // your unique project id
              
                // optional - identify your customer
                customer: { 
                  customer_id: "123", 
                  customer_name: "Our Best Customer, Inc.", 
                }
              });
              
              // call the send function (within an async function block) WITH THE SERVICE GROUP ID as shown 
              // (parameters: prompt, messages, stream response? true/false, caching allowed? true/false, serviceGroupId, abortController, onCompleteCallback, onErrorCallback)
              await send("1+1=", [], [], false, true, "group:${title ?? ""}");
              console.log(response);`
                    );
                  }}
                >
                  Copy Code
                </Button>

                {selectedCount > 0 && (
                  <Chip
                    label={`${selectedCount} selected`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
            <Divider sx={{ mt: 2, borderColor: "action.disabled" }} />
          </Box>
        </Box>{" "}
        <List dense>
          {models.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="No models available"
                secondary="Contact support to add models to this group"
              />
            </ListItem>
          ) : (
            groupModelsByHost(models).map((hostGroup, hostIndex) => (
              <React.Fragment key={hostGroup.host}>
                {" "}
                {/* Host Header */}                <ListSubheader
                  sx={{
                    borderRadius: 1,
                    lineHeight: "36px",
                    px: 2,
                    py: 1,
                    mb: 1,
                    fontWeight: "medium",
                    bgcolor: "transparent",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "text.primary" }}
                    >
                      Hosted on {hostGroup.host}
                    </Typography>
                    {(hostGroup.host === "OpenAI" || 
                      hostGroup.host === "Anthropic" || 
                      hostGroup.host === "Google") && (
                      <Chip
                        label="MCP support"
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{ height: 18, fontSize: "0.7rem" }}
                      />
                    )}
                  </Box>
                </ListSubheader>
                {/* Model Groups under this host */}
                {hostGroup.modelGroups.map((modelGroup, groupIndex) => (
                  <React.Fragment
                    key={`${hostGroup.host}-${modelGroup.vendor}-${modelGroup.name}`}
                  >
                    {/* Model Group Header */}
                    <ListSubheader
                      sx={{
                        bgcolor: "transparent",
                        lineHeight: "32px",
                        px: 2,
                        py: 0.5,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            bgcolor: getVendorColor(modelGroup.vendor),
                            fontSize: "0.65rem",
                          }}
                        >
                          {getVendorIcon(modelGroup.vendor)}
                        </Avatar>
                        <Typography variant="subtitle2" color="text.primary">
                          {modelGroup.vendor} {modelGroup.name}
                        </Typography>
                      </Box>
                    </ListSubheader>{" "}
                    {/* Individual Models */}
                    {modelGroup.models.map((model) => {
                      const modelKey = getModelKey(model);
                      const isSelected = selectedModels.includes(modelKey);

                      // Check if model requires API key and if it's configured
                      const requiresAmazonKey = model.host === "Amazon";
                      const requiresAzureKey =
                        model.host === "Microsoft" || model.host === "Azure";
                      const isDisabled =
                        (requiresAmazonKey && !hasAmazonApiKey) ||
                        (requiresAzureKey && !hasAzureApiKey);

                      return (
                        <ListItem
                          key={model.id}
                          sx={{
                            pl: 6, // Double indent to show it's under both host and model group
                            py: 0.25, // Reduced padding for more compact display
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                        >
                          {" "}                          <ListItemText
                            secondary={
                              <Box display="flex" alignItems="center" gap={1}>
                                {isDisabled && (
                                  <Chip
                                    label="API Key Required"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: "0.7rem" }}
                                  />
                                )}
                                {model.status === "beingDeprecated" && (
                                  <Chip
                                    label="being deprecated"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: "0.7rem" }}
                                  />
                                )}
                                {model.defaultEnabled && (
                                  <Chip
                                    label="recommended"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: "0.7rem" }}
                                  />
                                )}                                {model.performanceRating && (
                                  <Chip
                                    label={model.performanceRating}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: "0.7rem" }}
                                  />
                                )}
                                {model.supportsSearch && (
                                  <Chip
                                    label="search"
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: "0.7rem" }}
                                  />
                                )}
                                {model.thinkingSupport && (
                                  <Chip
                                    label="reasoning"
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: "0.7rem" }}
                                  />
                                )}
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {formatTokenPrices(
                                    model.inputTokenPrice,
                                    model.outputTokenPrice
                                  )}
                                </Typography>
                              </Box>
                            }
                          />{" "}
                          {isAdmin && onAdminEditModel && (
                            <IconButton
                              size="small"
                              onClick={() => onAdminEditModel(model.id)}
                              sx={{ mr: 1 }}
                              title="Edit Model (Admin)"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}{" "}
                          {onModelTest && (
                            <IconButton
                              size="small"
                              onClick={() => handleTestModel(model)}
                              disabled={
                                testingModels.has(model.id) || isDisabled
                              }
                              sx={{
                                mr: 1,
                                color: testResults.has(model.id)
                                  ? testResults.get(model.id)
                                    ? "success.main"
                                    : "error.main"
                                  : "inherit",
                              }}
                              title={
                                isDisabled
                                  ? "API Key required - configure in settings"
                                  : testingModels.has(model.id)
                                  ? "Testing..."
                                  : testResults.has(model.id)
                                  ? testResults.get(model.id)
                                    ? "Test Passed"
                                    : "Test Failed"
                                  : "Test Model"
                              }
                            >
                              {testingModels.has(model.id) ? (
                                <CircularProgress size={16} />
                              ) : testResults.has(model.id) ? (
                                testResults.get(model.id) ? (
                                  <SuccessIcon fontSize="small" />
                                ) : (
                                  <ErrorIcon fontSize="small" />
                                )
                              ) : (
                                <TestIcon fontSize="small" />
                              )}
                            </IconButton>
                          )}{" "}
                          <Switch
                            checked={isSelected}
                            onChange={() => onModelToggle(modelKey)}
                            size="small"
                            disabled={isDisabled}
                            title={
                              isDisabled
                                ? "Configure API keys in settings to enable this model"
                                : ""
                            }
                          />
                        </ListItem>
                      );
                    })}
                    {/* Add small gap between model groups */}
                    {groupIndex < hostGroup.modelGroups.length - 1 && (
                      <Box sx={{ height: 8 }} />
                    )}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))
          )}
        </List>
        {models.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: "block" }}
          >
            Toggle models to add them to this service group. Multiple models
            provide redundancy.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
